import { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FileSpreadsheet, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

import {
  deleteSummaryOnlyImports,
  importExpenseSummaryRows,
  parseExpenseSummaryFile,
  type ExpenseSummaryImportResult,
  type ParsedExpenseSummaryFile
} from '@/lib/invoice-summary-import'
import { formatCurrency, getSpecialtyLabel } from '@/lib/medical-invoices'

interface ExpenseSummaryImporterProps {
  patientId: string
  onImported?: (result?: ExpenseSummaryImportResult) => void
}

function SummaryStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-dark-border bg-dark-surface-high px-3 py-2">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-white">{value}</p>
    </div>
  )
}

export default function ExpenseSummaryImporter({
  patientId,
  onImported
}: ExpenseSummaryImporterProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [parsedFile, setParsedFile] = useState<ParsedExpenseSummaryFile | null>(null)
  const [importResult, setImportResult] = useState<ExpenseSummaryImportResult | null>(null)
  const [isParsing, setIsParsing] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isClearing, setIsClearing] = useState(false)

  const previewRows = useMemo(() => parsedFile?.rows.slice(0, 6) || [], [parsedFile])

  const resetState = useCallback(() => {
    setSelectedFile(null)
    setParsedFile(null)
    setImportResult(null)
    setIsParsing(false)
    setIsImporting(false)
  }, [])

  const handleFileSelection = useCallback(async (file: File) => {
    setSelectedFile(file)
    setImportResult(null)
    setIsParsing(true)

    try {
      const parsed = await parseExpenseSummaryFile(file)
      setParsedFile(parsed)

      if (parsed.totalRows === 0) {
        toast.error('Aucune ligne exploitable trouvée dans ce fichier')
      } else {
        toast.success(`${parsed.totalRows} ligne(s) reconnue(s)`)
      }
    } catch (error) {
      console.error('Erreur lecture récapitulatif:', error)
      toast.error(error instanceof Error ? error.message : 'Impossible de lire ce fichier')
      setParsedFile(null)
    } finally {
      setIsParsing(false)
    }
  }, [])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const [file] = acceptedFiles
    if (!file) {
      return
    }

    await handleFileSelection(file)
  }, [handleFileSelection])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    disabled: isParsing || isImporting,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls', '.csv'],
      'application/csv': ['.csv'],
      'text/csv': ['.csv'],
      'text/plain': ['.csv']
    }
  })

  const handleImport = useCallback(async () => {
    if (!parsedFile) {
      toast.error('Sélectionnez d’abord un fichier Excel ou CSV')
      return
    }

    setIsImporting(true)

    try {
      const result = await importExpenseSummaryRows({
        patientId,
        parsedFile
      })

      setImportResult(result)

      const summaryBits = [
        `${result.createdCount} créées`,
        `${result.updatedCount} rapprochées`,
        `${result.duplicateCount} doublons`
      ]
      toast.success(summaryBits.join(' • '))
      onImported?.(result)
    } catch (error) {
      console.error('Erreur import récapitulatif:', error)
      toast.error(error instanceof Error ? error.message : 'Import impossible')
    } finally {
      setIsImporting(false)
    }
  }, [onImported, parsedFile, patientId])

  const handleClearSummaryImports = useCallback(async () => {
    const sourceFileName = selectedFile?.name || null
    const confirmMessage = sourceFileName
      ? `Supprimer les lignes issues du récapitulatif "${sourceFileName}" ? Les vraies factures PDF resteront intactes.`
      : 'Supprimer les lignes issues des récapitulatifs Excel/CSV sans justificatif PDF ?'

    if (!window.confirm(confirmMessage)) {
      return
    }

    setIsClearing(true)

    try {
      const result = await deleteSummaryOnlyImports({
        patientId,
        sourceFileName
      })

      setImportResult(null)
      toast.success(
        result.deletedCount > 0
          ? `${result.deletedCount} ligne(s) récap supprimée(s)`
          : 'Aucune ligne récap à supprimer'
      )
      onImported?.()
    } catch (error) {
      console.error('Erreur suppression récapitulatif:', error)
      toast.error(error instanceof Error ? error.message : 'Suppression impossible')
    } finally {
      setIsClearing(false)
    }
  }, [onImported, patientId, selectedFile])

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`rounded-2xl border-2 border-dashed p-6 transition-colors ${
          isDragActive ? 'border-primary-400 bg-primary-900/10' : 'border-dark-border hover:border-primary-600'
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-dark-border bg-dark-surface-high">
            {isParsing ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary-300" />
            ) : (
              <FileSpreadsheet className="h-5 w-5 text-primary-300" />
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-white">
              {selectedFile ? selectedFile.name : 'Importer un récapitulatif Excel ou CSV'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Dépenses passées, remboursements Sécu, mutuelle, reste à charge.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <button type="button" className="btn-primary">
              <Upload className="h-4 w-4" />
              Choisir un fichier
            </button>
            {selectedFile && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  resetState()
                }}
                className="btn-ghost"
              >
                <RefreshCw className="h-4 w-4" />
                Recommencer
              </button>
            )}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                void handleClearSummaryImports()
              }}
              disabled={isParsing || isImporting || isClearing}
              className="btn-ghost text-rose-200 hover:text-rose-100"
            >
              {isClearing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Effacer imports Excel
            </button>
          </div>
        </div>
      </div>

      {parsedFile && (
        <div className="space-y-4 rounded-2xl border border-dark-border bg-dark-surface p-4">
          <div className="grid gap-3 md:grid-cols-4">
            <SummaryStat label="Lignes reconnues" value={parsedFile.totalRows} />
            <SummaryStat label="Lignes ignorées" value={parsedFile.skippedRows} />
            <SummaryStat label="Feuilles" value={parsedFile.sheets.length} />
            <SummaryStat
              label="Source"
              value={selectedFile ? `${(selectedFile.size / 1024 / 1024).toFixed(1)} MB` : '-'}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {parsedFile.sheets.map((sheet) => (
              <span
                key={sheet.name}
                className="rounded-full border border-dark-border bg-dark-surface-high px-3 py-1 text-xs text-slate-300"
              >
                {sheet.name}: {sheet.importedRows}
              </span>
            ))}
          </div>

          {previewRows.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-dark-border">
              <div className="grid grid-cols-[120px_1.5fr_120px_140px] gap-3 border-b border-dark-border bg-dark-surface-high px-3 py-2 text-xs uppercase tracking-wide text-slate-500">
                <span>Date</span>
                <span>Praticien</span>
                <span>Montant</span>
                <span>Spécialité</span>
              </div>

              <div className="divide-y divide-dark-border">
                {previewRows.map((row) => (
                  <div
                    key={`${row.sheetName}-${row.rowIndex}`}
                    className="grid grid-cols-[120px_1.5fr_120px_140px] gap-3 px-3 py-2 text-sm"
                  >
                    <span className="text-slate-300">{row.input.service_date}</span>
                    <div className="min-w-0">
                      <p className="truncate text-white">{row.input.practitioner_name}</p>
                      {row.input.notes && (
                        <p className="truncate text-xs text-slate-500">{row.input.notes}</p>
                      )}
                    </div>
                    <span className="font-medium text-white">{formatCurrency(row.input.total_amount)}</span>
                    <span className="text-slate-300">{getSpecialtyLabel(row.input.specialty)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              Seules les lignes avec une vraie date, un montant et un contexte de soin clair sont importées.
            </p>

            <button
              type="button"
              onClick={handleImport}
              disabled={isImporting || isClearing || parsedFile.totalRows === 0}
              className="btn-primary"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Importer dans Factures
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {importResult && (
        <div className="grid gap-3 md:grid-cols-4">
          <SummaryStat label="Créées" value={importResult.createdCount} />
          <SummaryStat label="Rapprochées" value={importResult.updatedCount} />
          <SummaryStat label="Doublons" value={importResult.duplicateCount} />
          <SummaryStat label="Ignorées" value={importResult.skippedCount} />
        </div>
      )}

      {importResult?.errors.length ? (
        <div className="rounded-2xl border border-amber-800/40 bg-amber-900/10 p-4">
          <p className="text-sm font-medium text-amber-200">Lignes à vérifier</p>
          <div className="mt-2 space-y-1 text-xs text-amber-100/80">
            {importResult.errors.slice(0, 6).map((error) => (
              <p key={error}>{error}</p>
            ))}
            {importResult.errors.length > 6 && (
              <p>… {importResult.errors.length - 6} autre(s) ligne(s)</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
